import { AlertCircle, Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export function cx(...values: (string | false | null | undefined)[]): string {
    return values.filter(Boolean).join(" ");
}

/**
 * The app mark: two offset flashcards, the front one carrying the German flag's three
 * stripes.
 *
 * Inlined rather than pointed at /logo.svg with an <img>, because it sits in the header on
 * every route and an <img> would flash empty for one request on a cold load. The geometry
 * is duplicated in public/logo.svg, which is what the browser tab uses -- an icon
 * referenced from <link rel="icon"> is rendered outside the document, so it cannot share
 * this markup. Edit both or they drift.
 *
 * Colours are literals for the same reason the SVG file's are: #3263c3 is --color-brand
 * resolved to sRGB. The mark is one fixed object, not a themed component -- it looks the
 * same in light and dark, which is what makes it recognisable as a favicon.
 *
 * Size and position come from `className` (e.g. "size-7"); the rounded corners are part of
 * the artwork, so no border-radius is needed on the element.
 */
export function Logo({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 32 32" className={cx("block", className)} aria-hidden focusable="false">
            <rect width="32" height="32" rx="8.5" fill="#3263c3" />
            <rect
                x="6.8"
                y="8"
                width="13"
                height="16.5"
                rx="2.6"
                fill="#ffffff"
                fillOpacity=".45"
                transform="rotate(-14 16 16)"
            />
            <g transform="rotate(8 16 16)">
                <rect x="10.5" y="7.5" width="13.5" height="17" rx="2.6" fill="#ffffff" />
                <rect x="13.2" y="10.8" width="8.1" height="1.7" rx=".85" fill="#1a1a1a" />
                <rect x="13.2" y="14.2" width="8.1" height="1.7" rx=".85" fill="#dd0000" />
                <rect x="13.2" y="17.6" width="8.1" height="1.7" rx=".85" fill="#ffce00" />
            </g>
        </svg>
    );
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
    primary: "bg-brand text-white hover:bg-brand-strong border-transparent",
    secondary: "bg-surface text-ink border-line hover:border-brand hover:text-brand",
    ghost: "bg-transparent text-ink-soft border-transparent hover:bg-surface-sunken hover:text-ink",
    danger: "bg-transparent text-bad border-line hover:bg-bad-soft"
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    loading?: boolean;
}

export function Button({ variant = "primary", loading, className, children, disabled, ...rest }: ButtonProps) {
    return (
        <button
            {...rest}
            disabled={disabled || loading}
            className={cx(
                "inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium",
                "transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                BUTTON_VARIANTS[variant],
                className
            )}
        >
            {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {children}
        </button>
    );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
    return (
        <div className={cx("rounded-2xl border border-line bg-surface", className)}>{children}</div>
    );
}

export function Spinner({ label = "加载中" }: { label?: string }) {
    return (
        <div className="flex items-center justify-center gap-2 py-12 text-ink-soft" role="status">
            <Loader2 className="size-5 animate-spin" aria-hidden />
            <span className="text-sm">{label}</span>
        </div>
    );
}

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
    return (
        <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-bad/40 bg-bad-soft px-4 py-3 text-sm text-ink"
        >
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-bad" aria-hidden />
            <div className="flex-1">{message}</div>
            {onRetry ? (
                <button onClick={onRetry} className="shrink-0 font-medium text-bad underline underline-offset-2">
                    重试
                </button>
            ) : null}
        </div>
    );
}

export function EmptyState({
    title,
    description,
    action
}: {
    title: string;
    description?: string;
    action?: ReactNode;
}) {
    return (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line px-6 py-14 text-center">
            <p className="text-base font-medium text-ink">{title}</p>
            {description ? <p className="max-w-md text-sm text-ink-soft">{description}</p> : null}
            {action}
        </div>
    );
}

type BadgeTone = "neutral" | "brand" | "good" | "warn" | "bad";

const BADGE_TONES: Record<BadgeTone, string> = {
    neutral: "bg-surface-sunken text-ink-soft border-line",
    brand: "bg-brand-soft text-brand border-brand/30",
    good: "bg-good-soft text-good border-good/30",
    warn: "bg-warn-soft text-warn border-warn/30",
    bad: "bg-bad-soft text-bad border-bad/30"
};

export function Badge({
    tone = "neutral",
    title,
    children
}: {
    tone?: BadgeTone;
    /** Hover text, for badges whose single word needs a sentence of explanation. */
    title?: string;
    children: ReactNode;
}) {
    return (
        <span
            title={title}
            className={cx(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                BADGE_TONES[tone]
            )}
        >
            {children}
        </span>
    );
}

/** Horizontal progress bar. `value` and `max` are in the same arbitrary unit. */
export function ProgressBar({ value, max, tone = "brand" }: { value: number; max: number; tone?: BadgeTone }) {
    const percent = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
    const fill =
        tone === "good" ? "bg-good" : tone === "warn" ? "bg-warn" : tone === "bad" ? "bg-bad" : "bg-brand";
    return (
        <div
            className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
            role="progressbar"
            aria-valuenow={value}
            aria-valuemin={0}
            aria-valuemax={max}
        >
            <div className={cx("h-full rounded-full transition-[width] duration-300", fill)} style={{ width: `${percent}%` }} />
        </div>
    );
}
